<script setup>
/**
 * Profile → RM via text (RM-430 part G; its own tile on the Profile, Kapil 10/24).
 *
 * A Platinum member turns RM via text on for the mobile on their profile. The
 * words beside the button are the consent: they come from the server, which
 * saves exactly what was shown. Turning texts off stays where it is: reply
 * Unsubscribe to any RM text. While the app way is off (production, until
 * Twilio approves it) only a member whose texting is already on gets here; the
 * tile is hidden for everyone else, and this page sends them back.
 */
import axios from '@axios'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useUsersStore } from '@/stores'
import { rmViaTextVisible } from '@/utils/rmViaText'

const router = useRouter()
const usersStore = useUsersStore()
const { myUser, isEditingMyUser } = storeToRefs(usersStore)

// Back goes to the member's own Profile, the screen the tile lives on.
const backTo = computed(() => (myUser.value?.profileId ? `/m/${myUser.value.profileId}` : '/m/'))

const state = ref(null)
const loading = ref(true)
const turningOn = ref(false)
const error = ref('')

async function load() {
  try {
    const { data } = await axios.get('/agent/sms/me')
    state.value = data
  }
  catch (e) {
    state.value = null
  }
  finally {
    loading.value = false
  }
  if (!rmViaTextVisible(state.value))
    router.replace(backTo.value)
}

async function turnOn() {
  turningOn.value = true
  error.value = ''
  try {
    await axios.post('/agent/sms/me/turn-on', {})
    await load()
  }
  catch (e) {
    error.value = e?.response?.data?.error || 'RM via text couldn\'t be turned on. Please try again.'
  }
  finally {
    turningOn.value = false
  }
}

// "Add mobile number" opens Edit Profile on the Mobile Phone field (Kapil 10/26); when
// it closes, read the state again so a saved number shows the Turn on button.
watch(isEditingMyUser, (open, wasOpen) => {
  if (wasOpen && !open)
    load()
})

onMounted(load)
</script>

<template>
  <div class="rmtext-page">
    <div class="max-content-width rmtext-page__nav">
      <RouterLink
        :to="backTo"
        class="rmtext-page__back d-flex align-center cursor-pointer"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        <span class="ml-1">Back</span>
      </RouterLink>
    </div>

    <div class="max-content-width rmtext-page__body">
      <h1 class="rmtext-page__title">
        RM via text
      </h1>

      <VSkeletonLoader
        v-if="loading"
        type="paragraph, button"
      />

      <VCard
        v-else-if="state"
        class="rmtext-page__card"
      >
        <template v-if="state.on">
          <p class="rmtext-page__status">
            On for {{ state.number }}
          </p>
          <p
            v-if="state.rmNumber"
            class="rmtext-page__meta"
          >
            Text RM anytime at {{ state.rmNumber }}
          </p>
        </template>

        <template v-else-if="state.canTurnOn">
          <p class="rmtext-page__description">
            {{ state.description }}
          </p>
          <!-- The consent disclosures, kept separate from the description (Kapil and the product agent 10/26) -->
          <div class="rmtext-page__disclosure">
            <p class="mb-1">
              {{ state.disclosure }}
            </p>
            <a
              href="/terms"
              target="_blank"
            >Terms</a> · <a
              href="/privacy"
              target="_blank"
            >Privacy Policy</a>
          </div>
          <VAlert
            v-if="error"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ error }}
          </VAlert>
          <VBtn
            color="primary"
            :loading="turningOn"
            block
            class="rmtext-page__action"
            @click="turnOn"
          >
            {{ state.label }}
          </VBtn>
        </template>

        <template v-else-if="state.reason === 'no_mobile'">
          <p class="rmtext-page__meta mb-4">
            Add a mobile number to your profile to use RM via text
          </p>
          <VBtn
            color="primary"
            block
            class="rmtext-page__action"
            @click="usersStore.editMyUser('phoneNumber')"
          >
            Add mobile number
          </VBtn>
        </template>
        <p
          v-else-if="state.reason === 'opted_out'"
          class="rmtext-page__meta"
        >
          Texts from RM were turned off from your phone. Text Start to {{ state.rmNumber || 'the RM number' }} to turn them back on.
        </p>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rmtext-page {
  width: 100%;
}

.rmtext-page__nav {
  padding: 16px 16px 0;
}

.rmtext-page__back {
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(0, 0, 0, 0.6));
  text-decoration: none;

  &:hover {
    color: var(--ds-text-primary, rgba(0, 0, 0, 0.87));
  }
}

.rmtext-page__body {
  padding: 16px 16px 48px;
}

.rmtext-page__title {
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: 700;
}

.rmtext-page__card {
  padding: 16px;
}

.rmtext-page__status {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.rmtext-page__description {
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 1.5;
}

.rmtext-page__disclosure {
  margin: 0 0 16px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));

  p {
    margin: 0;
  }

  a {
    color: rgb(var(--v-theme-primary));
  }
}

.rmtext-page__meta {
  margin: 0;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.rmtext-page__action {
  min-height: 44px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
